import * as THREE from 'three';
import { clamp, hash01, hash3, lerp, mulberry32, smoothstep } from '@/core/rng';
import { surfaceHeight } from '@/world/chunk';
import type { Region } from '@/world/region';

/**
 * The island's geology: outcrops on the ridges and crowns, scree under the
 * steepest faces.
 *
 * A 2.6 km heightfield with nothing on it reads as upholstery — there is
 * nothing between the grass underfoot and the coastline to tell you how big a
 * mountain is. Stone is the missing scale reference, so it must be *earned*:
 * this walks the region grid once and asks each cell whether the land there
 * could hold soil at all. High ground, steep faces, convex spurs and dry
 * country show their bedrock; flat wet lowland never does. Where a cell
 * qualifies it gets one deterministic cluster — an outcrop with a core block
 * and satellites, not confetti — seeded from the cell's own coordinates, so
 * the same island always carries the same stones.
 *
 * Built once from the immutable Region like the rivers, three InstancedMeshes,
 * zero per-frame cost. `update` only decides whether the small debris is worth
 * drawing; the big outcrops are landmarks and stay.
 */

export interface Rocks {
  group: THREE.Group;
  /** Camera orbit radius in metres; far views can drop the small debris. */
  update(camR: number): void;
  /**
   * The outcrop nearest a point, for the pointer to name.
   *
   * A linear scan of the big rocks' own positions, which is five thousand
   * comparisons and about twenty microseconds — against the eight
   * *milliseconds* raycasting them costs, because one instanced mesh holding
   * the whole island has an island-sized bounding sphere and every ray
   * therefore tests every instance in it. Scree is deliberately not offered:
   * it is gravel, and pointing at gravel means pointing at the ground.
   */
  nearestTo(x: number, z: number, within: number): { x: number; y: number; z: number; size: number } | null;
  dispose(): void;
}

/** Instance budget for the whole 50 km island. Clamps, not quotas. */
const BIG_CAP = 6000;
const SCREE_CAP = 17000;
/** Each big shape needs a little over half the budget: the draw is ~50/50. */
const SHAPE_CAP = Math.ceil(BIG_CAP * 0.6);

/** Scree is surface texture, and texture stops carrying beyond this. */
const SCREE_VIS = 2500;
/** Outcrops are landmarks — they stay long after the debris has gone. */
const BIG_VIS = 14000;

// ---- the placement rule: what makes a cell bare enough to show its stone ----
/** Bare-stone score a cell must reach to host an outcrop. */
const HOST = 1.10;
/** Hashed slack on that threshold, so its boundary is a coast, not a contour. */
const DITHER = 0.16;
/** Relative elevation over which the land pales toward alpine rock. */
const REL0 = 0.52, REL1 = 0.95;
/** Gradient (metres per metre, over one cell) over which a face sheds its soil. */
const SLOPE0 = 0.18, SLOPE1 = 0.52;
/** Metres of cell-wide convexity that count as a full spur. */
const CRV_NORM = 70;
/** Gradient above which a face also sheds a talus fan below it. */
const SCREE_SLOPE = 0.44;
/** Metres above the sea a cell needs before it can hold stone at all. */
const SEA_CLEAR = 3;
/** No boulder is ever seated at or under the waterline. */
const DRY_FOOT = 1.5;

const TAU = Math.PI * 2;
/** The dry earth and the pale alpine grey of `groundColour`'s slope ramp. */
const EARTH = [126 / 255, 110 / 255, 84 / 255] as const;
const ALPINE = [0.58, 0.595, 0.61] as const;

/**
 * One low-poly boulder. The base polyhedron's vertices are pushed around by a
 * hash of their own position — so the five or seven triangles meeting at a
 * corner all move together and the shell never cracks — then squashed, so a
 * shape sits broad or stands slabbed rather than looking spherical. Vertex
 * colour darkens the foot and bleaches the crown, which is most of the relief
 * these twenty faces will ever show.
 *
 * All of it happens once, at build time: every instance is this same rock seen
 * from a different angle, at a different size.
 */
function boulderGeometry(
  base: THREE.BufferGeometry, seed: number, amp: number, squash: number,
): THREE.BufferGeometry {
  const pos = base.attributes.position as THREE.BufferAttribute;
  const a = pos.array as Float32Array;
  const col = new Float32Array(a.length);
  for (let i = 0; i < pos.count; i++) {
    const o = i * 3;
    const x = a[o], y = a[o + 1], z = a[o + 2];
    // key on the vertex's own coordinates: duplicates hash alike, so they stay welded
    const kx = Math.round(x * 4096), ky = Math.round(y * 4096), kz = Math.round(z * 4096);
    const h1 = hash01(kx, ky * 7919 + kz, seed);
    const h2 = hash01(ky, kz * 7919 + kx, seed + 1);
    const h3 = hash01(kz, kx * 7919 + ky, seed + 2);
    const ny = y + (h2 - 0.5) * amp;
    a[o] = x + (h1 - 0.5) * amp;
    a[o + 1] = ny * squash;
    a[o + 2] = z + (h3 - 0.5) * amp;
    const shade = lerp(0.60, 1.02, smoothstep(-0.32, 0.42, ny));
    col[o] = col[o + 1] = col[o + 2] = shade;
  }
  base.setAttribute('color', new THREE.BufferAttribute(col, 3));
  base.deleteAttribute('uv');
  base.computeVertexNormals();   // non-indexed, so one normal per face: hard edges
  base.computeBoundingSphere();
  return base;
}

/**
 * WHAT THIS MODULE KNOWS HOW TO DRAW, separated from where it knows to put it.
 *
 * A squat lumpy mass, a taller slab, and flat chips for the debris — modelled
 * about their own centres at a mean diameter of one metre, so a caller scales
 * by the size it wants. `buildRocks` is the game's only caller and seats them
 * on the island; `src/workshop/` is the other, and it needs the stone without
 * an island under it. Split out rather than duplicated: two boulder builds
 * drifting apart is exactly how a lab stops being evidence about the game.
 */
export function rockShapes():
    { block: THREE.BufferGeometry; slab: THREE.BufferGeometry; chip: THREE.BufferGeometry } {
  return {
    block: boulderGeometry(new THREE.IcosahedronGeometry(0.5, 0), 0x51ce, 0.30, 0.86),
    slab: boulderGeometry(new THREE.DodecahedronGeometry(0.5, 0), 0x2b17, 0.24, 1.08),
    chip: boulderGeometry(new THREE.IcosahedronGeometry(0.5, 0), 0x7a33, 0.34, 0.72),
  };
}

/** The stone material. Vertex colour carries the foot-to-crown ramp. */
export const rockMaterial = (): THREE.MeshStandardMaterial =>
  new THREE.MeshStandardMaterial({
    roughness: 0.9, metalness: 0, vertexColors: true, flatShading: true,
  });

export function buildRocks(region: Region): Rocks {
  const { n, cell, seed } = region;
  const half = (n / 2) * cell;
  const centre = (i: number) => (i + 0.5) * cell - half;
  const peak = region.maxHeight * region.heightScale;

  const group = new THREE.Group();
  group.name = 'rocks';
  const stone = rockMaterial();
  const shapes = rockShapes();
  const rockA = new THREE.InstancedMesh(shapes.block, stone, SHAPE_CAP);
  const rockB = new THREE.InstancedMesh(shapes.slab, stone, SHAPE_CAP);
  const scree = new THREE.InstancedMesh(shapes.chip, stone, SCREE_CAP);
  rockA.name = 'rocksA'; rockB.name = 'rocksB'; scree.name = 'scree';

  // build-time scratch; nothing here runs per frame
  const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _e = new THREE.Euler();
  const _spin = new THREE.Quaternion(), _n = new THREE.Vector3();
  const _p = new THREE.Vector3(), _s = new THREE.Vector3(), _c = new THREE.Color();
  const UP = new THREE.Vector3(0, 1, 0);

  /**
   * Cool grey, warmed toward the ramp's dry earth low down, paled up high.
   * Written straight into the scratch colour: 18,000 stones is 18,000 tuples
   * of garbage otherwise, and the whole build wants to fit in a frame or two.
   */
  const tint = (g: number, rel: number): THREE.Color => {
    const warm = (1 - rel) * 0.34, pale = smoothstep(0.55, 0.94, rel) * 0.28;
    const ch = (base: number, earth: number, alp: number) =>
      lerp(lerp(base, earth, warm), alp, pale);
    return _c.setRGB(
      ch(g * 0.97, EARTH[0], ALPINE[0]),
      ch(g, EARTH[1], ALPINE[1]),
      ch(g * 1.05, EARTH[2], ALPINE[2]));
  };

  /**
   * Seat one stone. `size` is its mean diameter; the three axes vary
   * independently around it and it is buried a tenth of its own height or so,
   * so the ground closes over its foot and nothing looks dropped on the
   * surface. Returns false where the stone would stand in water.
   *
   * The block is spun freely but *tipped to the ground*: an arbitrary tilt on
   * a 30° face drives one corner into the hill and leaves a thin sliver of the
   * far edge standing out, which reads as a shard of glass rather than as
   * stone. Leaning it most of the way onto the local surface normal is both
   * what a seated boulder does and what keeps its broad face to the sky.
   */
  const seat = (
    mesh: THREE.InstancedMesh, i: number,
    px: number, pz: number, size: number, rel: number,
    gx: number, gz: number, rnd: () => number,
    /**
     * 0..1: how much of the flat-ground extra bury this stone takes. A block
     * on a slope seats itself — the uphill side digs in — but on level ground
     * the old shallow bury left every face standing clear of the surface,
     * which is the single loudest "dropped prop" read there is. A big block
     * on the flat now sinks like the erratic it is, up to a third of its
     * height; scree passes a fraction, or the small stuff would vanish.
     */
    sink = 1,
  ): boolean => {
    // never so flat that the tip turns the block into a plate on edge
    const sy = size * (0.78 + rnd() * 0.45);
    const sx = size * (0.85 + rnd() * 0.3);
    const sz = size * (0.85 + rnd() * 0.3);
    // enough that the ground closes over the foot — and much more on level
    // ground, where nothing else will close over it (see `sink`)
    const flat = 1 - smoothstep(0.10, 0.45, Math.hypot(gx, gz));
    const bury = sy * (0.07 + rnd() * 0.11) * (1 + flat * 1.5 * sink);
    const spin = rnd() * TAU;
    const lean = 0.7;   // how far onto the surface normal, 1 being flush
    const wobbleX = (rnd() - 0.5) * 0.22, wobbleZ = (rnd() - 0.5) * 0.22;
    const grey = lerp(0.42, 0.54, rnd());
    const ground = surfaceHeight(region, px, pz, 1);
    if (ground < DRY_FOOT) return false;
    _p.set(px, ground - bury, pz);
    _n.set(-gx * lean + wobbleX, 1, -gz * lean + wobbleZ).normalize();
    _e.set(0, spin, 0);
    _spin.setFromEuler(_e);
    _q.setFromUnitVectors(UP, _n).multiply(_spin);   // spin first, then tip
    _s.set(sx, sy, sz);
    _m.compose(_p, _q, _s);
    mesh.setMatrixAt(i, _m);
    mesh.setColorAt(i, tint(grey, rel));
    return true;
  };

  let nA = 0, nB = 0, nS = 0;

  for (let z = 1; z < n - 1; z++) for (let x = 1; x < n - 1; x++) {
    const wx = centre(x), wz = centre(z);
    const site = region.siteAt(wx, wz);
    if (!site.land) continue;
    const h = region.heightAt(wx, wz);
    if (h < SEA_CLEAR) continue;

    // what the land says about bare stone here: how high it stands, how
    // steeply it falls, whether it is a spur or a hollow, how dry it is
    const rel = clamp(h / peak, 0, 1);
    const gx = (region.heightAt(wx + cell / 2, wz) - region.heightAt(wx - cell / 2, wz)) / cell;
    const gz = (region.heightAt(wx, wz + cell / 2) - region.heightAt(wx, wz - cell / 2)) / cell;
    const slope = Math.hypot(gx, gz);
    // chunk.ts's convexity() samples at ±90 m, and a bilinear field has no
    // curvature inside one 260 m cell — so this stencil is a full cell wide
    const crv = clamp((h * 4
      - (region.heightAt(wx + cell, wz) + region.heightAt(wx - cell, wz)
       + region.heightAt(wx, wz + cell) + region.heightAt(wx, wz - cell))) / CRV_NORM, 0, 1);
    const score = smoothstep(REL0, REL1, rel) * 0.95
                + smoothstep(SLOPE0, SLOPE1, slope)
                + crv * 0.5
                + clamp(0.34 - site.rain, 0, 1) * 0.375;
    // flat ground that is not high country is meadow, whatever else it scores
    if (slope < 0.14 && rel < 0.5) continue;
    const hosts = score + (hash01(x, z, seed + 733) - 0.5) * 2 * DITHER >= HOST;

    if (hosts && nA + nB < BIG_CAP) {
      const rnd = mulberry32(hash3(x, z, seed));
      const strength = clamp((score - HOST) / 0.5, 0, 1);
      const many = clamp(3 + Math.round(strength * 7 * (0.75 + rnd() * 0.5)), 3, 12);
      // one outcrop, not a sprinkle: the cluster keeps to a blob inside the cell
      const bx = wx + (rnd() - 0.5) * cell * 0.55;
      const bz = wz + (rnd() - 0.5) * cell * 0.55;
      // and the blob is a band along the contour, not a disc — bedrock breaks
      // out *along* a ridge or a bench, so an outcrop is long and thin, which
      // is also what gives a crest a stone silhouette instead of stone freckles
      const along = 18 + rnd() * 38;
      const alongX = slope > 1e-4 ? -gz / slope : 1;
      const alongZ = slope > 1e-4 ? gx / slope : 0;
      // steep, high ground breaks into bigger blocks than a gentle shoulder
      const mass = clamp(0.3 + slope * 0.9 + rel * 0.5, 0, 1);

      for (let k = 0; k < many && nA + nB < BIG_CAP; k++) {
        const pick = rnd();
        // the first block is the core and sits at the middle; the next two are
        // its buttresses; everything after that is fallen debris around them
        const u = k === 0 ? 0 : (rnd() - 0.5) * 2 * along;
        const v = k === 0 ? 0 : (rnd() - 0.5) * 0.6 * along;
        const grade = k === 0 ? 1 : k < 3 ? 0.55 + rnd() * 0.45 : Math.pow(rnd(), 1.5);
        const size = lerp(3.4, 21.5, grade * (0.35 + mass * 0.65));
        const useA = nB >= SHAPE_CAP || (nA < SHAPE_CAP && pick < 0.5);
        if (useA ? nA >= SHAPE_CAP : nB >= SHAPE_CAP) break;
        const mesh = useA ? rockA : rockB;
        const px = bx + alongX * u - alongZ * v;
        const pz = bz + alongZ * u + alongX * v;
        const ok = seat(mesh, useA ? nA : nB, px, pz, size, rel, gx, gz, rnd);
        if (ok) {
          if (useA) nA++; else nB++;
          // A block sheds a skirt of its own rubble. This is most of what
          // separates a boulder that helped make the ground it stands on
          // from a prop set down on it: the transition from block to soil
          // happens through stone of falling size, not across one hard edge.
          if (size >= 6) {
            const bits = 3 + Math.round(rnd() * 4);
            for (let r = 0; r < bits && nS < SCREE_CAP; r++) {
              const a = rnd() * TAU;
              const d = size * (0.55 + rnd() * 0.6);
              const bit = Math.max(0.6, size * (0.07 + rnd() * 0.13));
              if (seat(scree, nS, px + Math.cos(a) * d, pz + Math.sin(a) * d,
                       bit, rel, gx, gz, rnd, 0.4)) nS++;
            }
          }
        }
      }
    }

    if (slope > SCREE_SLOPE && nS < SCREE_CAP) {
      const rnd = mulberry32(hash3(x, z, seed + 0x5c1e));
      const steep = clamp((slope - SCREE_SLOPE) / 0.22, 0, 1);
      const many = 7 + Math.round(steep * 14);
      // the fan runs downhill from the face, crowded at its foot and thinning
      // outward, its stones getting finer as they run out of momentum
      const dx = -gx / slope, dz = -gz / slope;
      for (let k = 0; k < many && nS < SCREE_CAP; k++) {
        const t = Math.pow(rnd(), 1.6);
        const d = t * cell * 0.85;
        const fan = (rnd() - 0.5) * cell * 0.5 * (0.35 + t);
        const size = lerp(0.8, 2.7, Math.pow(rnd(), 1.4) * (1 - t * 0.3));
        if (seat(scree, nS, wx + dx * d - dz * fan, wz + dz * d + dx * fan,
                 size, rel, gx, gz, rnd, 0.35)) nS++;
      }
    }
  }

  rockA.count = nA; rockB.count = nB; scree.count = nS;
  for (const m of [rockA, rockB, scree]) {
    // one mesh spans the island, so its bounds are the island: culling it by
    // them can only ever throw the whole thing away at the wrong moment
    m.frustumCulled = false;
    m.receiveShadow = true;
    m.instanceMatrix.needsUpdate = true;
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
    group.add(m);
  }
  // The two block meshes cast; the scree does not. A boulder without a shadow
  // is the "rocks look dropped" note in Griffin's own polish brief, and it is
  // literal — nothing but clouds cast anything in this world. Scree is 340k
  // triangles of gravel whose stones are smaller than the shadow texel at any
  // altitude the map is sized for, so it would cost a third of the pass to
  // describe nothing.
  for (const m of [rockA, rockB]) m.castShadow = true;

  // the big rocks' own positions, read back out of the matrices they were
  // just composed into — one flat pass, so the pointer never has to ask the
  // ray about sixteen thousand stones
  const bigN = nA + nB;
  const bigX = new Float64Array(bigN), bigY = new Float64Array(bigN);
  const bigZ = new Float64Array(bigN), bigR = new Float64Array(bigN);
  {
    let i = 0;
    for (const [m, count] of [[rockA, nA], [rockB, nB]] as const) {
      const a = m.instanceMatrix.array as Float32Array;
      for (let k = 0; k < count; k++, i++) {
        const o = k * 16;
        bigX[i] = a[o + 12]; bigY[i] = a[o + 13]; bigZ[i] = a[o + 14];
        bigR[i] = Math.max(Math.hypot(a[o], a[o + 1], a[o + 2]),
                           Math.hypot(a[o + 8], a[o + 9], a[o + 10]));
      }
    }
  }

  return {
    group,
    update(camR) {
      scree.visible = camR < SCREE_VIS;
      rockA.visible = rockB.visible = camR < BIG_VIS;
    },
    nearestTo(x, z, within) {
      let best = -1, bestD = within;
      for (let i = 0; i < bigN; i++) {
        // the stone's own radius counts as reach: a big block is easier to
        // mean than a small one, which is also true of pointing at it
        const d = Math.hypot(bigX[i] - x, bigZ[i] - z) - bigR[i];
        if (d < bestD) { bestD = d; best = i; }
      }
      return best < 0 ? null
        : { x: bigX[best], y: bigY[best], z: bigZ[best], size: bigR[best] };
    },
    dispose() {
      for (const m of [rockA, rockB, scree]) {
        group.remove(m);
        m.geometry.dispose();
        m.dispose();
      }
      stone.dispose();
    },
  };
}
